import { useCallback } from 'react';
import { ChatSignalREvent, CHAT_SIGNAL_R_HUB_URL } from './constants';
import { useCreateSignalREffect } from 'common/hooks/use-create-signal-r-effect';
import { useProfileStore } from 'common/services/profile-service/service-store/service-store';
import { profileServiceDataSelector } from 'common/services/profile-service/service-store/selectors';
import { useChatUnreadActions } from 'common/services/chat-unread-service/hooks';
import { useChatMembersActions } from '../chat-members-service/hooks';
import { useChatMessagesActions } from '../chat-messages-service/hooks';
import { useChatMessagesStore } from '../chat-messages-service/service-store/service-store';
import { useChatsListActions } from '../chats-list-service/hooks';
import { useChatsListStore } from '../chats-list-service/service-store/service-store';
import { useSelectedChatActions } from '../selected-chat-service/hooks';
import { useSelectedChatStore } from '../selected-chat-service/service-store/service-store';
import { HUB_URL } from 'common/constants/common';
export var useChatSignalR = function () {
    var currentUser = useProfileStore(profileServiceDataSelector);
    var useSignalREffect = useCreateSignalREffect(CHAT_SIGNAL_R_HUB_URL, HUB_URL).useSignalREffect;
    var _a = useChatUnreadActions(), incrementUnreadMessagesCount = _a.incrementUnreadMessagesCount, refreshUnreadMessagesCount = _a.refreshUnreadMessagesCount;
    var invalidateChatMembersAction = useChatMembersActions().invalidateChatMembersAction;
    var _b = useChatsListActions(), invalidateChatsListCacheAction = _b.invalidateChatsListCacheAction, markChatAsRead = _b.markChatAsRead, refreshChatsListAction = _b.refreshChatsListAction, removeChat = _b.removeChat, renameChat = _b.renameChat, updateChatParams = _b.updateChatParams, updateChatLastMessage = _b.updateChatLastMessage, editChatLastMessage = _b.editChatLastMessage;
    var _c = useChatMessagesActions(), appendMessage = _c.appendMessage, editMessage = _c.editMessage, invalidateLatestChatMessagesAction = _c.invalidateLatestChatMessagesAction, rememberUnreadMessage = _c.rememberUnreadMessage, removeMessage = _c.removeMessage;
    var excludeSelectedChat = useSelectedChatActions().excludeSelectedChat;
    var handleAppendedToChat = useCallback(function (_event) {
        refreshChatsListAction();
        refreshUnreadMessagesCount();
    }, [refreshChatsListAction, refreshUnreadMessagesCount]);
    var handleExcludedFromChat = useCallback(function (_a) {
        var chatId = _a.chatId;
        if (useSelectedChatStore.getState().selectedChatId === chatId) {
            excludeSelectedChat(chatId);
        }
        else {
            removeChat(chatId);
        }
        invalidateChatsListCacheAction();
        refreshUnreadMessagesCount();
    }, [excludeSelectedChat, invalidateChatsListCacheAction, refreshUnreadMessagesCount, removeChat]);
    var handleMembersChanged = useCallback(function (_a) {
        var chatId = _a.chatId;
        invalidateChatMembersAction(chatId);
        refreshChatsListAction();
    }, [invalidateChatMembersAction, refreshChatsListAction]);
    var handleChatParamsUpdated = useCallback(function (_a) {
        var chatId = _a.chatId, title = _a.title, description = _a.description;
        updateChatParams({ chatId: chatId, title: title, description: description });
        invalidateChatsListCacheAction();
    }, [invalidateChatsListCacheAction, updateChatParams]);
    var handleChatRenamed = useCallback(function (_a) {
        var chatId = _a.chatId, title = _a.title;
        renameChat({ chatId: chatId, title: title });
        invalidateChatsListCacheAction();
    }, [invalidateChatsListCacheAction, renameChat]);
    var handleReceiveMessage = useCallback(function (_a) {
        var _b;
        var chatId = _a.chatId, message = _a.message;
        var chat = useChatsListStore.getState().content.find(function (_a) {
            var id = _a.id;
            return id === chatId;
        });
        var isOwnMessage = !message.isSystem && ((_b = message.author) === null || _b === void 0 ? void 0 : _b.id) === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id);
        var isOpenedChat = useSelectedChatStore.getState().selectedChatId === chatId && useChatMessagesStore.getState().chatId === chatId;
        var isChatVisible = isOpenedChat && useChatMessagesStore.getState().isAtBottom;
        if (!isChatVisible && !isOwnMessage) {
            incrementUnreadMessagesCount();
            if (!isOpenedChat)
                rememberUnreadMessage(chatId, message.id);
        }
        if (!chat) {
            refreshChatsListAction();
            invalidateLatestChatMessagesAction(chatId);
            return;
        }
        updateChatLastMessage({
            chatId: chatId,
            message: message,
            incrementUnread: !isChatVisible && !isOwnMessage,
        });
        invalidateChatsListCacheAction();
        invalidateLatestChatMessagesAction(chatId);
        if (isOpenedChat) {
            appendMessage(message, !isOwnMessage && !isChatVisible);
        }
    }, [
        appendMessage,
        currentUser,
        incrementUnreadMessagesCount,
        invalidateChatsListCacheAction,
        invalidateLatestChatMessagesAction,
        rememberUnreadMessage,
        refreshChatsListAction,
        updateChatLastMessage,
    ]);
    var handleMessageRemoved = useCallback(function (_a) {
        var _b;
        var chatId = _a.chatId, messageId = _a.messageId;
        var chat = useChatsListStore.getState().content.find(function (_a) {
            var id = _a.id;
            return id === chatId;
        });
        if (useChatMessagesStore.getState().chatId === chatId) {
            removeMessage(messageId);
        }
        invalidateLatestChatMessagesAction(chatId);
        if (!chat || ((_b = chat.lastMessage) === null || _b === void 0 ? void 0 : _b.id) === messageId) {
            refreshChatsListAction();
            refreshUnreadMessagesCount();
            return;
        }
        invalidateChatsListCacheAction();
        refreshUnreadMessagesCount();
    }, [
        invalidateChatsListCacheAction,
        invalidateLatestChatMessagesAction,
        refreshChatsListAction,
        refreshUnreadMessagesCount,
        removeMessage,
    ]);
    var handleMessageEdited = useCallback(function (_a) {
        var chatId = _a.chatId, message = _a.message;
        if (useChatMessagesStore.getState().chatId === chatId) {
            editMessage(message);
        }
        editChatLastMessage({ chatId: chatId, message: message });
        invalidateChatsListCacheAction();
        invalidateLatestChatMessagesAction(chatId);
    }, [editChatLastMessage, editMessage, invalidateChatsListCacheAction, invalidateLatestChatMessagesAction]);
    var handleMessagesMarkedAsRead = useCallback(function (_a) {
        var chatId = _a.chatId;
        var isOpenedChat = useSelectedChatStore.getState().selectedChatId === chatId && useChatMessagesStore.getState().chatId === chatId;
        markChatAsRead(chatId);
        invalidateChatsListCacheAction();
        if (!isOpenedChat) {
            refreshUnreadMessagesCount();
        }
    }, [invalidateChatsListCacheAction, markChatAsRead, refreshUnreadMessagesCount]);
    useSignalREffect(ChatSignalREvent.AppendedToChat, handleAppendedToChat, [handleAppendedToChat]);
    useSignalREffect(ChatSignalREvent.MembersAppendedToChat, handleMembersChanged, [handleMembersChanged]);
    useSignalREffect(ChatSignalREvent.MembersExcludedFromChat, handleMembersChanged, [handleMembersChanged]);
    useSignalREffect(ChatSignalREvent.ExcludedFromChat, handleExcludedFromChat, [handleExcludedFromChat]);
    useSignalREffect(ChatSignalREvent.ChatParamsUpdated, handleChatParamsUpdated, [handleChatParamsUpdated]);
    useSignalREffect(ChatSignalREvent.ChatRenamed, handleChatRenamed, [handleChatRenamed]);
    useSignalREffect(ChatSignalREvent.ReceiveMessage, handleReceiveMessage, [handleReceiveMessage]);
    useSignalREffect(ChatSignalREvent.MessageEdited, handleMessageEdited, [handleMessageEdited]);
    useSignalREffect(ChatSignalREvent.MessageRemoved, handleMessageRemoved, [handleMessageRemoved]);
    useSignalREffect(ChatSignalREvent.MessagesMarkedAsRead, handleMessagesMarkedAsRead, [handleMessagesMarkedAsRead]);
};
